import React from 'react';
import ReactDOM from 'react-dom/client';
import '@cloudscape-design/global-styles/index.css';
// Must come after Cloudscape's styles: it overrides the icon slot's sizing.
import './styles/avatar.css';
import App from './App';
import { initializeRum } from './rum';
import { ensureAuthenticated, isAuthStuck, redirectToMidway } from './auth/midway';

// Fire-and-forget: RUM init reads settings.json and no-ops on localhost or
// when RUM config is absent. Never blocks or breaks app render.
void initializeRum();

/**
 * Minimal auth-error screen shown only when the Midway redirect loop guard has
 * tripped (repeated redirects failed to yield a usable token). Avoids leaving
 * the user on a blank page or in an infinite redirect loop.
 */
function AuthError() {
  return (
    <div
      style={{
        fontFamily: 'system-ui, sans-serif',
        maxWidth: 480,
        margin: '15vh auto',
        padding: '0 24px',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: 20 }}>Sign-in required</h1>
      <p style={{ color: '#555' }}>
        We couldn't complete Midway authentication. Make sure you're signed in to Midway, then try
        again.
      </p>
      <button
        style={{ padding: '8px 16px', cursor: 'pointer' }}
        onClick={() => {
          // Reset the loop guard and start a fresh sign-in attempt.
          sessionStorage.removeItem('tickety-midway-redirect-count');
          redirectToMidway();
        }}
      >
        Sign in with Midway
      </button>
    </div>
  );
}

/**
 * Gate the SPA behind Midway authentication. If no valid token is present the
 * app redirects to Midway before rendering (and this function returns without
 * mounting React, since the page is navigating away).
 */
async function bootstrap(): Promise<void> {
  const authenticated = await ensureAuthenticated();

  const root = ReactDOM.createRoot(document.getElementById('root')!);

  if (!authenticated) {
    // ensureAuthenticated triggers a redirect unless the loop guard tripped.
    if (isAuthStuck()) {
      root.render(
        <React.StrictMode>
          <AuthError />
        </React.StrictMode>,
      );
    }
    // Otherwise a redirect is in flight — leave the page blank while navigating.
    return;
  }

  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

void bootstrap();
